import { useEffect } from "react";
import { tenderSchema } from "@workspace/public-seo";
import { useRoute, Link, useLocation } from "wouter";
import { tenderSlug } from "@/lib/tenderSlug";
import { useGetTender, getGetTenderQueryKey, useListTenders } from "@workspace/api-client-react";
import { useCheckFavourite, useToggleFavourite } from "@/lib/useFavourites";
import { SeoHead } from "@/components/SeoHead";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { useUser } from "@clerk/react";
import {
  Lock, ArrowLeft, MapPin, Calendar, Building2,
  Phone, Mail, User, FileText, Link as LinkIcon,
  Clock, MapPinned, ChevronRight, Star,
} from "lucide-react";

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  catering: "午膳招標涵蓋學校飯堂外判、午膳供應商甄選等項目。供應商須符合教育局膳食指引，提供均衡營養膳食予學生。",
  it: "IT 及資訊科技招標包括電腦硬件採購、軟件授權、網絡基礎設施、STEM 設備及技術支援服務等項目。",
  engineering: "工程招標涵蓋學校建築維修、翻新工程、冷氣系統安裝、消防設備、電力及管道工程等。",
  cleaning: "清潔服務招標包括校舍日常清潔、消毒服務、廢物處理及環境衞生管理等外判合約。",
  security: "保安服務招標涵蓋校園保安人員外判、閉路電視系統、門禁管理及緊急應變等。",
  insurance: "保險招標包括學生平安保險、教職員保障、校舍財物保險及各類校園活動保障計劃。",
  printing: "印刷招標涵蓋校刊、教材、宣傳品、信封信紙及各類學校印刷品的製作外判。",
  furniture: "傢俬及設備招標包括課室桌椅、儲物設備、辦公室傢俬、圖書館書架及其他學校設備採購。",
  uniform: "制服招標涵蓋學生校服、體育服、教職員制服等的設計、製造及供應合約。",
  stationery: "文具及教材招標包括練習簿、文具用品、教學材料及學校消耗品的定期採購。",
  "air-conditioning": "冷氣及空調招標涵蓋冷氣機採購、中央冷氣系統、定期保養服務及翻新工程。",
  "fire-services": "消防設備招標包括消防系統安裝、滅火器採購、灑水系統、年度消防安全檢查及維護服務。",
  "social-work-counseling": "輔導及社工服務招標涵蓋學生輔導員、社工服務外判、心理健康支援及危機介入服務。",
  "speech-therapy-special-ed": "言語治療及特殊教育招標包括言語治療師外判、特殊學習需要支援、感統訓練及教育評估服務。",
  "sports-music": "體育及音樂招標涵蓋體育活動外判、音樂課程、樂器採購、校隊訓練及課外活動服務。",
  "ai-robotics": "AI 及機械人科技招標包括人工智能教育套件、機械人器材、編程課程、STEM 教學設備採購及技術培訓服務。",
  "growth-sky": "成長天空招標涵蓋學生成長支援、生命教育、課後學習及多元發展計劃相關服務及資源。",
  other: "其他類別招標包括各類學校日常運作所需的採購及外判服務，歡迎向學校查詢詳情。",
};

function RelatedTenderCard({ tender }: { tender: { id: number; title: string; district?: string | null; deadlineDate?: string | null; status?: string } }) {
  const slug = tenderSlug(tender.id, tender.title ?? "");
  const isOpen = tender.status === "open";
  return (
    <Link href={`/tenders/${slug}`}>
      <div className="flex items-start justify-between gap-3 py-3 hover:bg-secondary/30 rounded-md px-2 -mx-2 transition-colors cursor-pointer group">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-snug group-hover:text-primary line-clamp-2">{tender.title}</p>
          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
            {tender.district && <span>{tender.district}</span>}
            {tender.deadlineDate && <span>截止：{tender.deadlineDate}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isOpen ? (
            <Badge className="text-[10px] px-1.5 py-0 text-[#2a6f4f] border-[#2a6f4f] bg-[#95ff0040]">招標中</Badge>
          ) : (
            <Badge className="text-[10px] px-1.5 py-0 text-red-600 border-red-300 bg-red-50">已截止</Badge>
          )}
          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
        </div>
      </div>
    </Link>
  );
}

const RELATED_TENDER_POOL_SIZE = 30;

function recommendationScore(seed: number, candidateId: number): number {
  let value =
    Math.imul(seed ^ 0x9e3779b9, 0x85ebca6b) ^
    Math.imul(candidateId, 0xc2b2ae35);
  value ^= value >>> 16;
  value = Math.imul(value, 0x27d4eb2d);
  return (value ^ (value >>> 15)) >>> 0;
}

function diversifyRecommendations<T extends { id: number }>(tenders: T[], currentId: number): T[] {
  return tenders
    .filter(t => t.id !== currentId)
    .sort((a, b) => (
      recommendationScore(currentId, a.id) - recommendationScore(currentId, b.id)
    ))
    .slice(0, 4);
}

export default function TenderDetailPage() {
  const [, params] = useRoute("/tenders/:id");
  const [, navigate] = useLocation();
  const id = parseInt(params?.id ?? "0", 10);
  const { isSignedIn, isLoaded, user } = useUser();
  const signedIn = isLoaded && !!isSignedIn;

  // Favourites (paid members only — enabled after isPaid is known)
  const { data: favData } = useCheckFavourite(id, signedIn);
  const isFavourited = favData?.isFavourited ?? false;
  const toggleFav = useToggleFavourite();

  function handleBack() {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate("/tenders");
    }
  }

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  const { data: tender, isLoading } = useGetTender(id, {
    query: {
      enabled: !!id && isLoaded,
      queryKey: [...getGetTenderQueryKey(id), user?.id ?? "anonymous"],
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: "always",
    }
  });

  // Redirect any URL that doesn't match the canonical slug to the canonical form.
  useEffect(() => {
    if (!tender) return;
    const paramId = params?.id ?? "";
    const canonical = tenderSlug(tender.id, tender.title ?? "");
    if (paramId !== canonical) {
      navigate(`/tenders/${canonical}`, { replace: true });
    }
  }, [tender, params?.id]);

  const firstCategorySlug = tender?.categories?.[0]?.slug ?? null;
  const district = tender?.district ?? null;

  const { data: sameCategoryData } = useListTenders(
    { category: firstCategorySlug ?? undefined, limit: RELATED_TENDER_POOL_SIZE, status: "open" },
    { query: { enabled: !!firstCategorySlug && !!tender, queryKey: ["related-category", firstCategorySlug, id, RELATED_TENDER_POOL_SIZE] } }
  );
  const { data: sameDistrictData } = useListTenders(
    { district: district ?? undefined, limit: RELATED_TENDER_POOL_SIZE, status: "open" },
    { query: { enabled: !!district && !!tender, queryKey: ["related-district", district, id, RELATED_TENDER_POOL_SIZE] } }
  );

  const sameCategoryTenders = diversifyRecommendations(sameCategoryData?.tenders ?? [], id);
  const sameDistrictTenders = diversifyRecommendations(sameDistrictData?.tenders ?? [], id);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 md:px-8 py-8 max-w-3xl">
        <Skeleton className="h-8 w-40 mb-6" />
        <Skeleton className="h-10 w-full mb-4" />
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
        </div>
      </div>
    );
  }

  if (!tender) {
    return (
      <div className="container mx-auto px-4 md:px-8 py-16 text-center">
        <p className="text-muted-foreground">招標項目不存在或已被刪除</p>
        <Link href="/tenders">
          <Button variant="outline" className="mt-4">返回招標列表</Button>
        </Link>
      </div>
    );
  }

  const isOpen = tender.status === "open";
  const isPaid = !tender.schoolNameHidden;
  const docUrls: string[] = ((tender as any).documentUrls ?? []).filter(Boolean);
  const schoolWebsite = tender.schoolWebsite?.trim() || null;
  const referenceUrl = tender.referenceUrl?.trim() || null;

  const tenderStatus = isOpen ? "招標中" : "已截止";
  const categoryNames = tender.categories.map((c: any) => c.nameChinese).join("、");
  const seoDescription = [
    tender.title,
    tender.district ? `地區：${tender.district}` : "",
    categoryNames ? `類別：${categoryNames}` : "",
    tender.deadlineDate ? `截止日期：${tender.deadlineDate}` : "",
    tenderStatus,
    "香港學校招標資訊 - Skoltend",
  ].filter(Boolean).join("。");

  const categoryDesc = firstCategorySlug ? CATEGORY_DESCRIPTIONS[firstCategorySlug] : null;

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={tender.title}
        description={seoDescription.slice(0, 160)}
        keywords={`${tender.district ?? ""}學校招標,${categoryNames},香港學校tender,${tender.title}`}
        canonical={`/tenders/${encodeURIComponent(tenderSlug(tender.id, tender.title ?? ""))}`}
        structuredData={tenderSchema(tender)}
      />
      {/* Header */}
      <div className="border-b bg-secondary/20 py-6">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <div className="flex items-center justify-between mb-4">
            <button onClick={handleBack} className="flex cursor-pointer items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="button-back">
              <ArrowLeft className="h-4 w-4" /> 返回招標列表
            </button>
            {signedIn && isPaid && (
              <button
                onClick={() => toggleFav.mutate({ tenderId: id, isFavourited })}
                disabled={toggleFav.isPending}
                title={isFavourited ? "移除收藏" : "加入我的最愛"}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors px-3 py-1.5 rounded-full border ${
                  isFavourited
                    ? "border-amber-300 bg-amber-50 text-amber-600 hover:bg-amber-100"
                    : "border-border text-muted-foreground hover:border-amber-300 hover:text-amber-500"
                }`}
              >
                <Star className={`h-4 w-4 transition-all ${isFavourited ? "fill-amber-400 text-amber-400" : ""}`} />
                {isFavourited ? "已收藏" : "收藏"}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {isOpen ? (
              <Badge className="whitespace-nowrap inline-flex items-center rounded-md border px-2.5 py-0.5 font-semibold transition-colors hover-elevate shadow-xs text-xs text-[#2a6f4f] border-[#2a6f4f] bg-[#95ff0080]">招標中</Badge>
            ) : (
              <Badge className="whitespace-nowrap text-xs font-semibold bg-red-50 text-red-600 border border-red-300 hover:bg-red-50">已截止</Badge>
            )}
            {tender.categories.map((cat) => (
              <Badge key={cat.slug} variant="outline" className="text-xs font-normal bg-[#2a6f4f26] border-[#2a6f4f26]">{cat.nameChinese}</Badge>
            ))}
          </div>
          <h1 className="text-xl md:text-2xl font-bold leading-snug" data-testid="tender-title">{tender.title}</h1>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-8 max-w-3xl space-y-6">

        {/* Basic info cards — 學校名稱 | 截止日期 | 地區 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Building2 className="h-5 w-5 text-primary shrink-0" />
              <div>
                <div className="text-muted-foreground text-[16px]">學校名稱</div>
                {!isPaid ? (
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="school-name-locked">
                    <Lock className="h-3.5 w-3.5" />
                    <span>付費會員可見</span>
                  </div>
                ) : schoolWebsite ? (
                  <a
                    href={schoolWebsite}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block cursor-pointer font-medium text-[20px] hover:text-primary hover:underline"
                    data-testid="tender-school-link"
                  >
                    {tender.schoolName ?? "—"}
                  </a>
                ) : (
                  <div className="font-medium text-[20px]" data-testid="tender-school">{tender.schoolName ?? "—"}</div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Calendar className="h-5 w-5 text-primary shrink-0" />
              <div>
                <div className="text-muted-foreground text-[16px]">截止日期</div>
                <div className="font-medium text-[20px]" data-testid="tender-deadline">
                  {tender.deadlineDate}
                </div>
                {(tender as any).deadlineTime && (
                  <div className="flex items-center gap-1 text-muted-foreground mt-0.5 text-[20px]">
                    <Clock className="h-3 w-3" />
                    {(tender as any).deadlineTime}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <MapPin className="h-5 w-5 text-primary shrink-0" />
              <div>
                <div className="text-muted-foreground text-[16px]">地區</div>
                <div className="font-medium text-[20px]" data-testid="tender-district">{tender.district}</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Paid content */}
        {isPaid ? (
          <>
            {tender.description && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="font-semibold tracking-tight flex items-center gap-2 text-[24px]">
                    <FileText className="h-6 w-6 text-primary" />
                    招標詳情
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground whitespace-pre-wrap text-[20px]" data-testid="tender-description">
                    {tender.description}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Contact details */}
            {((tender as any).contactPerson || (tender as any).contactPhone || (tender as any).contactEmail ||
              (tender as any).contactAddress || tender.contactInfo) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="font-semibold tracking-tight flex items-center gap-2 text-[24px]">
                    <User className="h-6 w-6 text-primary" />
                    聯絡資料
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(tender as any).contactPerson && (
                    <div className="flex items-start gap-3">
                      <User className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <div className="text-muted-foreground mb-0.5 text-[16px]">聯絡人</div>
                        <div className="font-medium text-[20px]">{(tender as any).contactPerson}</div>
                      </div>
                    </div>
                  )}
                  {(tender as any).contactPhone && (
                    <>
                      {(tender as any).contactPerson && <Separator />}
                      <div className="flex items-start gap-3">
                        <Phone className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                        <div>
                          <div className="text-muted-foreground mb-0.5 text-[16px]">聯絡電話</div>
                          <a href={`tel:${(tender as any).contactPhone}`} className="font-medium hover:text-primary text-[20px]">
                            {(tender as any).contactPhone}
                          </a>
                        </div>
                      </div>
                    </>
                  )}
                  {(tender as any).contactEmail && (
                    <>
                      <Separator />
                      <div className="flex items-start gap-3">
                        <Mail className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                        <div>
                          <div className="text-muted-foreground mb-0.5 text-[16px]">電子郵件</div>
                          <a href={`mailto:${(tender as any).contactEmail}`} className="font-medium hover:text-primary break-all text-[20px]">
                            {(tender as any).contactEmail}
                          </a>
                        </div>
                      </div>
                    </>
                  )}
                  {(tender as any).contactAddress && (
                    <>
                      <Separator />
                      <div className="flex items-start gap-3">
                        <MapPinned className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                        <div>
                          <div className="text-muted-foreground mb-0.5 text-[16px]">地址</div>
                          <div className="font-medium text-[20px]">{(tender as any).contactAddress}</div>
                        </div>
                      </div>
                    </>
                  )}
                  {tender.contactInfo && (
                    <>
                      <Separator />
                      <div className="flex items-start gap-3">
                        <FileText className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                        <div>
                          <div className="text-muted-foreground mb-0.5 text-[16px]">補充聯絡資料</div>
                          <div className="font-medium text-[20px]">{tender.contactInfo}</div>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Reference link */}
            {referenceUrl && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="font-semibold tracking-tight flex items-center gap-2 text-[24px]">
                    <LinkIcon className="h-6 w-6 text-primary" />
                    參考連結
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a
                    href={referenceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary hover:underline break-all text-[16px]"
                    data-testid="tender-reference-link"
                  >
                    <LinkIcon className="h-5 w-5 shrink-0" />
                    查看學校網頁中的招標項目
                  </a>
                </CardContent>
              </Card>
            )}

            {/* Document links */}
            {docUrls.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="font-semibold tracking-tight flex items-center gap-2 text-[24px]">
                    <LinkIcon className="h-6 w-6 text-primary" />
                    招標文件連結
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {docUrls.map((url, idx) => (
                    <a
                      key={idx}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-primary hover:underline break-all text-[16px]"
                    >
                      <LinkIcon className="h-5 w-5 shrink-0" />
                      {url}
                    </a>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  招標詳情
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <p className="text-sm text-muted-foreground blur-sm select-none">
                    本校誠邀有意承辦之機構提交標書。承辦商須提供相關服務，投標者須持有有效牌照及相關證明。詳細要求請參閱招標文件。截楆日期前須提交密封投標書。
                  </p>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-background/90 backdrop-blur-sm rounded-lg p-4 text-center border shadow-sm">
                      <Lock className="h-6 w-6 text-primary mx-auto mb-2" />
                      <p className="text-sm font-medium">升級為付費會員以查看招標詳情</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="p-6 text-center">
                <Lock className="h-8 w-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold mb-2">{signedIn ? "升級為付費會員" : "升級查看完整招標資料"}</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  付費會員可查看學校名稱、聯絡人、電話、電郵、地址、參考連結及文件連結。
                </p>
                {signedIn ? (
                  <Link href="/pricing">
                    <Button data-testid="button-upgrade-from-detail-auth">立即升級</Button>
                  </Link>
                ) : (
                  <Link href="/sign-in" rel="nofollow">
                    <Button data-testid="button-upgrade-from-detail">登入查看完整資料</Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Category description */}
        {categoryDesc && (
          <Card className="bg-secondary/20 border-secondary">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                <span className="font-medium text-foreground">關於{categoryNames}招標：</span>
                {categoryDesc}
                {district && <>此招標位於香港<span className="font-medium text-foreground">{district}</span>，如需查看更多{district}學校招標，可使用地區篩選功能。</>}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Same category tenders */}
        {sameCategoryTenders.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold">
                  同類別招標推薦
                  {categoryNames && <span className="ml-1 text-muted-foreground font-normal text-sm">（{categoryNames}）</span>}
                </CardTitle>
                <Link href={`/tenders?category=${firstCategorySlug ?? ""}`}>
                  <span className="text-xs text-primary hover:underline cursor-pointer">查看全部</span>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="pt-0 divide-y divide-border">
              {sameCategoryTenders.map(t => (
                <RelatedTenderCard key={t.id} tender={t} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Same district tenders */}
        {sameDistrictTenders.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold">
                  {district}其他學校招標
                </CardTitle>
                <Link href={`/tenders?district=${encodeURIComponent(district ?? "")}`}>
                  <span className="text-xs text-primary hover:underline cursor-pointer">查看全部</span>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="pt-0 divide-y divide-border">
              {sameDistrictTenders.map(t => (
                <RelatedTenderCard key={t.id} tender={t} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Platform CTA */}
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground mb-3">
            Skoltend 每日整合全港中小學最新招標資訊，免費瀏覽招標標題、地區、類別及截止日期。
          </p>
          <Link href="/tenders">
            <Button variant="outline" size="sm">瀏覽所有招標</Button>
          </Link>
        </div>

      </div>
    </div>
  );
}
